<template>
  <div class="home-wrap">
    <div v-if="hasPermission">
      <el-row :gutter="30">
        <el-col :span="12" v-if="isAuth('finance:financial')">
          <!-- 饼图 Accepted Client / Pending Modified Client / Potential Client / Lost Client -->
          <accepted-client-pie></accepted-client-pie>
        </el-col>
        <el-col :span="12" v-if="isAuth('finance:financial')">
          <!-- 折线图 Accepted Client / Pending Modified Client / Potential Client / Lost Client -->
          <accepted-client-line></accepted-client-line>
        </el-col>
      </el-row>

      <el-card class="mt4 box-card" v-loading="!listLoaded">
        <div slot="header">
          <div class="customHeader">
            <el-radio-group v-if="listLoaded" v-model="supplierId" size="small">
              <el-radio-button label="">All suppliers</el-radio-button>
              <el-radio-button v-for="(item, index) in supplierList" :key="`radio-supplier-${item.id}`" :label="item.id">
                <i class="el-icon-back arrow arrow-left" :class="{ disabled: index === 0 }" @click.prevent.stop="moveSupplier(index, -1)"></i>
                <span>{{ item.companyName }}</span>
                <i
                  class="el-icon-right arrow arrow-right"
                  :class="{ disabled: index === supplierList.length - 1 }"
                  @click.prevent.stop="moveSupplier(index, 1)"
                ></i>
              </el-radio-button>
            </el-radio-group>
          </div>
        </div>

        <el-row :gutter="30" v-if="listLoaded">
          <el-col :span="12">
            <!-- Total Transaction / Total Turnover -->
            <total-transaction :supplierId="supplierId" ref="totalTransactionRef"></total-transaction>
          </el-col>
          <el-col :span="12">
            <!-- Total Unpaid / Total Turnover -->
            <total-unpaid :supplierId="supplierId" ref="totalUnpaidRef"></total-unpaid>
          </el-col>
          <el-col :span="12">
            <!-- Processing Time -->
            <processing-time :supplierId="supplierId"></processing-time>
          </el-col>
          <el-col :span="12">
            <!-- Statistics of Transaction Status at Each Stage -->
            <statistics-transaction-status :supplierId="supplierId" />
          </el-col>
        </el-row>

        <el-row :gutter="30" v-if="listLoaded">
          <el-col :span="12">
            <!-- Checker -->
            <checker :supplierId="supplierId"></checker>
          </el-col>
          <el-col :span="12">
            <!-- Reviewer -->
            <reviewer :supplierId="supplierId"></reviewer>
          </el-col>
        </el-row>

        <el-row v-if="listLoaded">
          <el-col :span="24">
            <engagement-trend :supplierId="supplierId"></engagement-trend>
          </el-col>
        </el-row>
      </el-card>
    </div>
    <div v-else class="home-empty">
      <el-empty description="No viewing permission"></el-empty>
    </div>
  </div>
</template>

<script>
import TotalTransaction from "./modules/TotalTransaction";
import TotalUnpaid from "./modules/TotalUnpaid";
import ProcessingTime from "./modules/ProcessingTime";
import Checker from "./modules/Checker";
import Reviewer from "./modules/Reviewer";
import AcceptedClientPie from "./modules/AcceptedClientPie";
import AcceptedClientLine from "./modules/AcceptedClientLine";
import StatisticsTransactionStatus from "./modules/StatisticsTransactionStatus";
import EngagementTrend from "./modules/EngagementTrend";
import { mapActions, mapGetters } from "vuex";

// localStorage keys：供应商选中项记忆 / 自定义排序记忆
const SUPPLIER_ID_KEY = "dashboardSupplierId";
const SUPPLIER_ORDER_KEY = "dashboardSupplierOrder";

export default {
  name: "Dashboard",
  data() {
    return {
      supplierId: "",
      supplierList: [],
      listLoaded: false, // supplier列表加载并校验完成后才挂载图表子组件
      isReady: false, // 首次初始化完成后才允许 watch 生效
    };
  },
  computed: {
    ...mapGetters("authority", ["activeRoleAuthority"]),
    permissionList() {
      return this.activeRoleAuthority?.permissionList || [];
    },
    hasPermission() {
      return this.permissionList.length > 0;
    },
  },
  watch: {
    supplierId(val) {
      // 记忆选中的供应商，下次进入页面自动恢复
      localStorage.setItem(SUPPLIER_ID_KEY, JSON.stringify(val === null || val === undefined ? "" : val));
    },
    permissionList: {
      deep: true,
      handler(val) {
        // 首次进入页面期间一律跳过，仅后续切换角色等场景触发
        if (!this.isReady) return;
        if (val && val.length > 0) {
          this.$nextTick(() => {
            this.$refs.totalTransactionRef && this.$refs.totalTransactionRef.init();
            this.$refs.totalUnpaidRef && this.$refs.totalUnpaidRef.init();
          });
        }
      },
    },
  },
  components: {
    TotalTransaction,
    TotalUnpaid,
    ProcessingTime,
    Checker,
    Reviewer,
    AcceptedClientPie,
    AcceptedClientLine,
    StatisticsTransactionStatus,
    EngagementTrend,
  },
  methods: {
    ...mapActions("currency", ["getCurrencyList"]),
    // 获取供应商列表
    getSupplierList() {
      return new Promise((resolve, reject) => {
        this.$http
          .get("/admin/supplier")
          .then(res => {
            this.supplierList = this.sortSupplierList(res.data || []);
            // 恢复记忆的选中项；已失效(供应商被删除/无权限)则保持 ""
            try {
              const saved = JSON.parse(localStorage.getItem(SUPPLIER_ID_KEY));
              if (saved && this.supplierList.some(i => i.id === saved)) {
                this.supplierId = saved;
              } else if (saved) {
                // 记忆的供应商已失效，清除脏数据
                localStorage.removeItem(SUPPLIER_ID_KEY);
              }
            } catch (e) {
              // 解析失败忽略，保持 ""
            }
            // 此时 supplierId 已是最终值，v-if 放行子组件挂载，保证每个子组件只请求一次
            this.listLoaded = true;
            resolve();
          })
          .catch(err => {
            reject(err);
          });
      });
    },
    // 按记忆的自定义顺序排列供应商，未记录的新供应商按默认顺序追加在后面
    sortSupplierList(list) {
      try {
        const savedOrder = JSON.parse(localStorage.getItem(SUPPLIER_ORDER_KEY) || "[]");
        const map = new Map(list.map(item => [item.id, item]));
        const sorted = savedOrder.map(id => map.get(id)).filter(Boolean);
        const rest = list.filter(item => !savedOrder.includes(item.id));
        return [...sorted, ...rest];
      } catch (e) {
        return list;
      }
    },
    // 左右箭头移动供应商，与相邻项交换位置
    moveSupplier(index, step) {
      const target = index + step;
      if (target < 0 || target >= this.supplierList.length) return;
      const list = [...this.supplierList];
      [list[index], list[target]] = [list[target], list[index]];
      this.supplierList = list;
      localStorage.setItem(SUPPLIER_ORDER_KEY, JSON.stringify(list.map(i => i.id)));
    },
  },
  async mounted() {
    // echarts 跟随页面变化
    window.onresize = () => {
      if (document.getElementById("engagement-trend-id")) {
        this.$echarts.init(document.getElementById("engagement-trend-id")).resize();
      }
      // 敏感数据 - 财务权限
      if (this.isAuth("finance:financial")) {
        if (document.getElementById("transaction-amount")) {
          this.$echarts.init(document.getElementById("transaction-amount")).resize();
        }
        if (document.getElementById("unpaid-amount")) {
          this.$echarts.init(document.getElementById("unpaid-amount")).resize();
        }
        if (document.getElementById("clientChunk")) {
          this.$echarts.init(document.getElementById("clientChunk")).resize();
        }
        if (document.getElementById("client-quantity-line")) {
          this.$echarts.init(document.getElementById("client-quantity-line")).resize();
        }
      }
    };
    try {
      // 列表(含选中项恢复校验)与货币列表并行加载
      await Promise.all([this.getSupplierList(), this.getCurrencyList()]);
    } catch (e) {
      // 任一接口失败都不应中断后续初始化；兜底放行，避免图表区空白
      this.listLoaded = true;
    }
    await this.$nextTick();
    if (this.hasPermission) {
      this.$refs.totalTransactionRef && this.$refs.totalTransactionRef.init();
      this.$refs.totalUnpaidRef && this.$refs.totalUnpaidRef.init();
    }
    this.isReady = true; // 首次初始化完成，后续 permissionList 变化交由 watch
  },
};
</script>

<style lang="scss" scoped>
.home-wrap {
  margin-left: 4px;

  .home-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: calc(100vh - 300px);
  }

  .box-card {
    min-height: 400px;
  }

  ::v-deep {
    .el-card {
      background-color: transparent !important;

      .el-card__header {
        padding: 14px 20px;

        .customHeader {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .el-radio-group {
          .el-radio-button__inner {
            color: #7987a0;
            font-size: 13px;
          }
          .el-radio-button__orig-radio:checked + .el-radio-button__inner {
            color: #fff;

            // 选中态为蓝色背景，箭头改为白色保证可见性
            .arrow {
              color: #fff;
            }
            .arrow.disabled {
              color: rgba(255, 255, 255, 0.5);
            }
          }

          // 箭头（位于 radio-button 内部）：始终显示
          .arrow {
            font-size: 12px;
            color: #7987a0;
            cursor: pointer;

            &.disabled {
              color: #c0c4cc;
              cursor: not-allowed;
            }
          }

          .arrow-left {
            margin-right: 6px;
          }
          .arrow-right {
            margin-left: 6px;
          }
        }
      }
    }

    .el-radio-button:focus:not(.is-focus):not(:active):not(.is-disabled) {
      box-shadow: none !important;
    }
  }
}
</style>
